import React from 'react';

const AUTOFILL = {
  WebkitBoxShadow: '0 0 0 1000px var(--surface-2) inset',
  WebkitTextFillColor: 'var(--text-primary)',
  caretColor: 'var(--text-primary)',
};

export const Button = ({ children, onClick, variant = 'primary', size = 'md', disabled, loading, style = {}, type = 'button', fullWidth }) => {
  const sizes = { sm: { padding: '6px 12px', fontSize: 12 }, md: { padding: '10px 20px', fontSize: 14 }, lg: { padding: '14px 28px', fontSize: 16 } };
  const variants = {
    primary:   { background: 'var(--primary)', color: '#000', boxShadow: '0 0 20px rgba(46,204,113,0.25)', fontWeight: 700 },
    secondary: { background: 'var(--surface-2)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' },
    danger:    { background: 'var(--danger)', color: '#fff' },
    ghost:     { background: 'transparent', color: 'var(--primary)', border: '1px solid var(--border-color)' },
    success:   { background: 'var(--success)', color: '#000' },
    outline:   { background: 'transparent', color: 'var(--primary)', border: '1px solid var(--primary)' },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled || loading}
      style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', gap:6, fontWeight:600, borderRadius:10, border:'none', transition:'all 0.2s', cursor: disabled||loading?'not-allowed':'pointer', opacity: disabled||loading?0.6:1, width: fullWidth?'100%':'auto', whiteSpace:'nowrap', ...sizes[size], ...variants[variant], ...style }}>
      {loading ? '⏳' : children}
    </button>
  );
};

export const Input = ({ label, error, hint, ...props }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <label style={{ display:'block', fontSize:13, fontWeight:600, color:'var(--text-secondary)', marginBottom:6 }}>{label}</label>}
    <input {...props} style={{ background:'var(--surface-2)', border:`1px solid ${error?'var(--danger)':'var(--border-color)'}`, borderRadius:10, color:'var(--text-primary)', padding:'10px 14px', fontSize:14, width:'100%', outline:'none', transition:'all 0.2s', ...AUTOFILL, ...props.style }} />
    {error && <div style={{ fontSize:12, color:'var(--danger)', marginTop:4 }}>⚠ {error}</div>}
    {hint  && <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:4 }}>{hint}</div>}
  </div>
);

export const Select = ({ label, children, error, ...props }) => (
  <div style={{ marginBottom: 16 }}>
    {label && <label style={{ display:'block', fontSize:13, fontWeight:600, color:'var(--text-secondary)', marginBottom:6 }}>{label}</label>}
    <select {...props} style={{ background:'var(--surface-2)', border:`1px solid ${error?'var(--danger)':'var(--border-color)'}`, borderRadius:10, color:'var(--text-primary)', padding:'10px 14px', fontSize:14, width:'100%', outline:'none' }}>{children}</select>
    {error && <div style={{ fontSize:12, color:'var(--danger)', marginTop:4 }}>⚠ {error}</div>}
  </div>
);

export const Card = ({ children, style = {}, onClick }) => (
  <div onClick={onClick} style={{ background:'var(--surface-1)', border:'1px solid var(--border-color)', borderRadius:'var(--radius-lg)', padding:24, transition:'all 0.2s', cursor: onClick?'pointer':'default', ...style }}>{children}</div>
);

export const Badge = ({ children, color = 'primary', style = {} }) => {
  const colors = {
    primary:  { bg:'rgba(46,204,113,0.15)',  color:'#2ECC71', border:'rgba(46,204,113,0.3)' },
    success:  { bg:'rgba(46,204,113,0.15)',  color:'#2ECC71', border:'rgba(46,204,113,0.3)' },
    danger:   { bg:'rgba(231,76,60,0.15)',   color:'#E74C3C', border:'rgba(231,76,60,0.3)' },
    warning:  { bg:'rgba(243,156,18,0.15)',  color:'#F39C12', border:'rgba(243,156,18,0.3)' },
    info:     { bg:'rgba(52,152,219,0.15)',  color:'#3498DB', border:'rgba(52,152,219,0.3)' },
    gray:     { bg:'rgba(128,128,128,0.12)', color:'var(--text-muted)', border:'rgba(128,128,128,0.2)' },
  };
  const c = colors[color] || colors.gray;
  return <span style={{ background:c.bg, color:c.color, border:`1px solid ${c.border}`, padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, whiteSpace:'nowrap', ...style }}>{children}</span>;
};

export const StatCard = ({ icon, label, value, sub, color = 'var(--primary)', style = {} }) => (
  <Card style={style}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
      <div>
        <div style={{ fontSize:11, color:'var(--text-muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>{label}</div>
        <div style={{ fontSize:30, fontWeight:800, color }}>{value}</div>
        {sub && <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:4 }}>{sub}</div>}
      </div>
      <div style={{ fontSize:28, opacity:0.7 }}>{icon}</div>
    </div>
  </Card>
);

export const Modal = ({ open, onClose, title, children, size = 'md' }) => {
  if (!open) return null;
  const widths = { sm:420, md:560, lg:780, xl:1000 };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16, overflowY:'auto' }} onClick={onClose}>
      <div style={{ background:'var(--surface-1)', border:'1px solid var(--border-color)', borderRadius:20, padding:32, width:'100%', maxWidth:widths[size], maxHeight:'90vh', overflowY:'auto' }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
          <h3 style={{ fontSize:18, fontWeight:800, color:'var(--text-primary)' }}>{title}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-muted)', fontSize:22, lineHeight:1, cursor:'pointer' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
};

export const PageHeader = ({ title, subtitle, action }) => (
  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28 }}>
    <div>
      <h1 style={{ fontSize:24, fontWeight:800, marginBottom:4, color:'var(--text-primary)' }}>{title}</h1>
      {subtitle && <p style={{ color:'var(--text-muted)', fontSize:14 }}>{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const ProgressBar = ({ value, max, label, color = 'var(--primary)' }) => {
  const pct = max===-1?0:max>0?Math.min(100,Math.round((value/max)*100)):0;
  const barColor = pct>=90?'var(--danger)':pct>=70?'var(--warning)':color;
  return (
    <div style={{ marginBottom:12 }}>
      {label && <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
        <span style={{ fontSize:13, color:'var(--text-secondary)' }}>{label}</span>
        <span style={{ fontSize:13, color:'var(--text-muted)' }}>{max===-1?`${value?.toLocaleString()} / ∞`:`${value?.toLocaleString()} / ${max?.toLocaleString()}`}</span>
      </div>}
      <div style={{ background:'var(--surface-2)', borderRadius:6, height:8, overflow:'hidden' }}>
        <div style={{ width:`${pct}%`, height:'100%', background:barColor, borderRadius:6, transition:'width 0.5s' }} />
      </div>
    </div>
  );
};

export const Logo = ({ size = 36 }) => (
  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
    <img src="/logo.png" alt="MS Discoveries" style={{ height:size, width:'auto', objectFit:'contain' }} />
  </div>
);

export const Alert = ({ type = 'info', children, style = {} }) => {
  const types = {
    info:    { bg:'rgba(52,152,219,0.1)',  border:'rgba(52,152,219,0.3)',  color:'#3498DB', icon:'ℹ️' },
    success: { bg:'rgba(46,204,113,0.1)',  border:'rgba(46,204,113,0.3)',  color:'#2ECC71', icon:'✅' },
    warning: { bg:'rgba(243,156,18,0.1)', border:'rgba(243,156,18,0.3)', color:'#F39C12', icon:'⚠️' },
    danger:  { bg:'rgba(231,76,60,0.1)',  border:'rgba(231,76,60,0.3)',  color:'#E74C3C', icon:'🚨' },
  };
  const t = types[type];
  return <div style={{ background:t.bg, border:`1px solid ${t.border}`, borderRadius:12, padding:'12px 16px', display:'flex', gap:10, ...style }}><span>{t.icon}</span><div style={{ fontSize:14, color:t.color }}>{children}</div></div>;
};
